import type { InferGetServerSidePropsType, GetServerSideProps } from 'next'
import { useLiveQuery } from '~/lib/sanity-live-query'

import Footer from '../components/Footer';
import Layout from '../components/Layout';
import { getGlobalData } from '../utils/global-data';
import SEO from '../components/SEO';
import Slider from '../components/Sliders/Silder';
import Image from 'next/image';
import HorizontalLine from '../components/HorizontalLine';
import InspirationSummary from '../components/InspirationSummary';
import TimberSection from '../components/TimberSection';
import Systems from '../components/Systems';
import AboutUs from '../components/AboutUs';
import StickyNavbar from '../components/StickyNavbar';

import { readToken } from '~/lib/sanity.api'
import { getClient } from '~/lib/sanity.client'
import { urlForImage } from '~/lib/sanity.image'
import { getPage, getInspirations, getSystemsByType, getAbouts, getSiteConfig,  
  pageBySlugQuery,
  systemsQueryType,
  inspirationsQuery,
  siteConfigQuery,
  timbersQuery,
  categoriesQuery,
  getCategories,
  getTimbers,
 } from '~/lib/sanity.queries'
import { type Page, System, Inspiration, About, SiteConfig, Timber, Category } from '~/lib/sanity.interfaces'
import type { SharedPageProps } from '~/pages/_app'

interface Query {
  [key: string]: string
}

export const getServerSideProps : GetServerSideProps<
  SharedPageProps & {
    page: Page,
    systems: System[],
    products: System[],
    inspirations: Inspiration[],
    abouts: About[],
    categories: Category[],
    timbers: Timber[],
    systemPage: Page,
    inspirationPage: Page,
    aboutUsPage: Page,
    siteConfig: SiteConfig,
  },
  Query
> = async ({ draftMode = false, params = {} }) => {
  const client = getClient(draftMode ? { token: readToken } : undefined)
  const page = await getPage(client, 'home')
  const systemPage = await getPage(client, 'systems')
  const inspirationPage = await getPage(client, 'inspiration') 
  const aboutUsPage = await getPage(client, 'about-us') 
  const systems = await getSystemsByType(client, ['system', 'simple'])
  const products = await getSystemsByType(client, ['product'])
  const categories = await getCategories(client)
  const timbers = await getTimbers(client)
  const inspirations = await getInspirations(client)
  const abouts = await getAbouts(client)
  const siteConfig = await getSiteConfig(client)

  if (!page) {
    return {
      notFound: true,
    }
  }
  const globalData = getGlobalData();

  return {
    props: {
      globalData,
      draftMode,
      token: draftMode ? readToken : '',
      page,
      systems,
      products,
      inspirations,
      abouts,
      categories,
      timbers,
      systemPage,
      inspirationPage,
      aboutUsPage,
      siteConfig,
    },
  }
}

export default function IndexPage(
  props: InferGetServerSidePropsType<typeof getServerSideProps>,
  globalData
) {
  const [page] = useLiveQuery(props.draftMode, props.page, pageBySlugQuery, {
    slug: props.page.slug.current,
  })
  const [systemPage] = useLiveQuery(props.draftMode, props.systemPage, pageBySlugQuery, {
    slug: props.systemPage.slug.current,
  })
  const [inspirationPage] = useLiveQuery(props.draftMode, props.inspirationPage, pageBySlugQuery, {
    slug: props.inspirationPage.slug.current,
  })
  const [aboutUsPage] = useLiveQuery(props.draftMode, props.aboutUsPage, pageBySlugQuery, {
    slug: props.aboutUsPage.slug.current,
  })
  const [siteConfig] = useLiveQuery(props.draftMode, props.siteConfig, siteConfigQuery)

  const [systems] = useLiveQuery<System[]>(props.draftMode, props.systems, systemsQueryType, { systemTypes:  ['system', 'simple']})
  const [products] = useLiveQuery<System[]>(props.draftMode, props.products, systemsQueryType, { systemTypes: ['product']})
  const [inspirations] = useLiveQuery<Inspiration[]>(props.draftMode, props.inspirations, inspirationsQuery)
  const [timbers] = useLiveQuery<Timber[]>(props.draftMode, props.timbers, timbersQuery)
  const [categories] = useLiveQuery<Category[]>(props.draftMode, props.categories, categoriesQuery)
  
  return (
    <Layout >
      <SEO title={page.seoTitle} description={page.seoDescription} keywords={page.seoKeywords} />
      <StickyNavbar siteConfig={siteConfig} />
      <main className="w-full bg-background nav-offset">        
        <div className="relative w-full h-full px-2 md:px-10 lg:px-10 mb-8 overflow-hidden">
        {page.mainCarousel.length && (
          <Image
            src={urlForImage(page.mainCarousel[0]).url()}
            alt="RK Timber"
            width={0}
            height={0}
            sizes="100vw"
            style={{ width: '100%', height: 'auto' }}
            priority
          />
        )}
        </div>
        <p className='text-lg md:text-2xl text-center mb-12 mx-2 md:mx-10 lg:mx-40 '>{page.excerpt}</p>
        <div className='mx-2 md:mx-10 lg:mx-10 mb-8'>
          <Slider />
        </div>
        <TimberSection timberApplications={page.timberApplications} timberSpecies={page.timberSpecies} categories={categories} timbers={timbers} />
        <HorizontalLine />        
        <Systems title="Timber Systems" description={systemPage.excerpt} systems={systems} />
        <HorizontalLine />
        <Systems title="Timber Products" description={null} systems={products} />
        <HorizontalLine />
        <InspirationSummary title="Inspiration" description={inspirationPage.excerpt} inspirations={inspirations} />
        <HorizontalLine />
        <AboutUs aboutUsPage={aboutUsPage}  />
      </main>
      <Footer siteConfig={siteConfig} />      
    </Layout>
  )
}
